import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';

import { useDetectClickOutside } from 'hooks/useDetectClickOutside';
import SubmitInputField from 'components/FormFields/SubmitInputField';
import DropdownButton from 'components/Button/DropdownButton/DropdownButton';
import APDropdown from 'components/Dropdown/APDropdown';
import * as actions from 'containers/App/actions';

import ChecklistDueDate from './Checklist.DueDate';
import ChecklistComments from './Checklist.Comments';
import ChecklistAssigned from './Checklist.Assigned';
import ChecklistVisibility from './Checklist.Visibility';

import './nc-checklist.scss';

const NCTask = ({
  // tasks props
  assignmentOptions,
  // task props
  id,
  name,
  details,
  completed,
  dueDate,
  timeframe,
  hasWedding,
  weddingItem,
  hasLead,
  hasComments,
  lead,
  assignments,
  module = '',    // custom props
  weddingId,      // project props
  leadId,         // lead props
  organizationId, // project props
  comments = [],
  reminders = [],
  // custom props
  referenceId = null,
  active = false,
  setActive,
  attributes,
  listeners,
  setNodeRef,
  className,
  style,
}) => {
  const dispatch = useDispatch();

  const pastDue = dueDate ? dayjs().isAfter(dayjs(dueDate)) : false;
  const userInfo = useSelector((state) => state.global.userInfo);
  const { id: userId, organization }  = userInfo;
  const { id: orgId } = organization || {};
  const isMyFocus = assignments.findIndex(assignment =>
    assignment.loginId === userId && assignment.focusLevel === 1) >= 0;
  const nameRef = React.useRef();

  const { clientVisible } = weddingItem || {};
  // Filters out clients in assignment list if the visibility is org members only.
  // This is only applicable for project tasks. Need to show all org members for
  // any other task.
  const assignmentOpts = assignmentOptions
    .filter(opt => clientVisible || module !== 'weddings' || opt.organizationId === orgId);

  const processedReminders = reminders.map(r => {
    return ({ definitionId: r.id, taskId: r.spec.payload.task_id, delta: r.spec.delta });
  });
  const scope = hasLead ? 'leads' : hasWedding ? 'weddings' : 'loginTasks';

  const {
    createNotificationTask,
    updateNotificationTask,
    deleteNotificationTask,
    createTaskReminder,
    updateTaskReminder,
    deleteTaskReminder,
    getSingleTaskInfo,
  } = bindActionCreators(actions, dispatch);

  const [isEditing, setTaskEdit] = useState(active);
  const [visibility, setVisibility] = useState(weddingItem ? clientVisible : true);
  const [isCompleted, setCompleted] = useState(completed);
  const [showDelete, setShowDelete] = useState(false);

  useEffect(() => {
    if (isEditing && nameRef.current) {
      nameRef.current.select();
    }

    if (!isEditing) {
      setActive(false);
    }

    if (isEditing) {
      getSingleTaskInfo({ id, module });
    }
  }, [isEditing]);

  const onEditMode = () => {
    setTaskEdit(true);
  };

  const onEditDone = () => {
    if (isEditing) setTaskEdit(false);
  };

  const createTask = () => {
    let variables;
    if (module === 'loginTasks') {
      variables = {
        module,
        input: {},
        referenceId,
      };
    }
    if (module === 'assignedTasks') {
      variables = {
        module,
        focusLevel: 1,
        input: {},
        referenceId,
      };
    }
    if (module === 'weddings') {
      variables = {
        module,
        weddingId,
        input: {},
        referenceId,
      };
    }
    if (module === 'leads') {
      variables = {
        module,
        leadId,
        input: {},
        referenceId,
      };
    }

    if (variables) {
      createNotificationTask({ variables });
      setActive(true);
    }
  };

  const deleteTask = () => {
    let variables;
    if (module === 'loginTasks' || module === 'assignedTasks') {
      variables = {
        module,
        id,
      };
    }
    if (module === 'weddings') {
      variables = {
        module,
        taskId: id,
        weddingId,
      };
    }
    if (module === 'leads') {
      variables = {
        module,
        taskId: id,
        leadId,
      };
    }

    if (variables) {
      deleteNotificationTask({ variables });
    }
  };

  const saveTask = (params) => {
    let variables;
    const newDueDate = params?.dueDate ? dayjs(params.dueDate).utc().local().format(): null;
    const formattedDate = dueDate ? dayjs(dueDate).utc().local().format() : null;
    if (module === 'loginTasks' || module === 'assignedTasks') {
      if (userId) {
        variables = {
          module,
          id,
          input: {
            task: {
              name,
              details,
              dueDate: newDueDate || formattedDate,
              completed,
              ...params,
            },
          },
        };
      }
    }
    if (module === 'weddings') {
      variables = {
        module,
        taskId: id,
        weddingId,
        input: {
          task: {
            name,
            details,
            dueDate: newDueDate || formattedDate,
            timeframe: timeframe ?? null,
            completed,
            assignmentNote: '',
            isPrivate: false,
            clientVisible: weddingItem.clientVisible,
            ...params,
          },
        },
      };

      // Remove timeframe/due date if the other is set.
      if (params.dueDate) {
        variables.input.task.timeframe = null;
      }
      else if (params.timeframe) {
        variables.input.task.dueDate = null;
      }
    }

    if (module === 'leads') {
      variables = {
        module,
        taskId: id,
        leadId,
        input: {
          task: {
            name,
            details,
            dueDate: newDueDate || formattedDate,
            completed,
            assignmentNote: '',
            ...params,
          },
        },
      };
    }

    if (variables) {
      updateNotificationTask({ variables });
    }
  };

  const getNameDecorator = () => {
    if (module === 'assignedTasks') {
      if (hasWedding && weddingItem && weddingItem.wedding) {
        const { projectType, firstnames, shortname } = weddingItem.wedding;
        return projectType === 'wedding' ? `[${ firstnames }] ` : `[${ shortname }] `;
      }
      if (hasLead && lead) {
        return `[Lead: ${lead.contactFullName}] `;
      }
    }
    return '';
  };

  const toggleCheck = (e) => {
    const completedState = isCompleted;
    e.stopPropagation();
    setCompleted(!completedState);
    saveTask({ completed: !completedState });
  };

  const updateName = (e) => {
    e.stopPropagation();
    if (e.target.value === name) return;
    saveTask({ name: e.target.value });
  };

  const updateDetails = (e) => {
    if (e.target.value === details) return;
    saveTask({ details: e.target.value });
  };

  const pushDueDate = (date, type = '') => {
    if (type === 'timeframe') {
      saveTask({ timeframe: date });
    }
    else {
      saveTask({ dueDate: date });
    }
  };

  const updateVisibility = (e) => {
    setVisibility(e.target.value === 'true');
    saveTask({ clientVisible: e.target.value === 'true' });
  };

  const ref = useDetectClickOutside({ onTriggered: onEditDone });

  const Indicator = () => {
    let char = '';
    const discussionId = hasComments ? 2 : details ? 1 : 0;
    if (discussionId === 2) {
      char = 'r';
    }
    else if (discussionId === 1) {
      char = 'd';
    } else {
      return null;
    }

    return (
      <span className="nc-checklist__indicator ap-icon">{char}</span>
    );
  };

  const deleteTrigger = (
    <DropdownButton
      className="APIconButton button--unstyled no-padding"
      name="remove"
    >
      e
    </DropdownButton>
  );

  const renderDeleteConfirmation = () => {
    const deleteItem = (e) => {
      e.stopPropagation();
      e.nativeEvent.stopImmediatePropagation();
      deleteTask();
      setShowDelete(false);
    };

    return (
      <div
        className="comment-delete"
      >
        <div className="comment-delete__message">
          Deleting this item will <strong>permanently</strong> remove it from the project's checklist.
        </div>
        <div className="comment-delete__actions">
          <button
            className="comment-delete__button comment-delete__button--cancel"
            onClick={() => setShowDelete(false)}
          >
            Cancel
          </button>
          <button
            className="comment-delete__button comment-delete__button--delete"
            onClick={deleteItem}
          >
            Delete
          </button>
        </div>
      </div>
    );
  };

  const dndProps = {
    ...attributes,
    ...(!isEditing && { ...listeners }),
    style,
  };

  return (
    <div
      {...dndProps}
      ref={setNodeRef}
    >
      <div
        ref={ref}
        className={clsx('nc-checklist', pastDue && 'past-due', isMyFocus && 'focus', className)}
      >
        <div name="controls">
          <APDropdown
            isOpen={showDelete}
            show={() => setShowDelete(true)}
            dismiss={() => setShowDelete(false)}
            menuClass="ap-dropdown__menu--delete-confirmation nc-checklist__delete-confirmation"
            triggerButton={deleteTrigger}
          >
            {renderDeleteConfirmation()}
          </APDropdown>
          <button
            className="APIconButton"
            name="add"
            onClick={createTask}
          >
            +
          </button>
        </div>
        <div className="nc-checklist__checkbox">
          <button
            className={clsx('checkbox', isCompleted && 'completed')}
            onClick={toggleCheck}
          >
            <span className="visually-hidden">not checked</span>
          </button>
        </div>
        <div
          className="nc-checklist__name"
          onClick={onEditMode}
        >
          <div
            className={clsx('nc-checklist__title-label', isCompleted && 'completed', isEditing && 'hidden')}
          >
            {getNameDecorator() + name}
            {name === '' && (
              <span className="placeholder">
                (Unnamed Task)
              </span>
            )}
          </div>
          <Indicator />
          {isEditing && (
            <SubmitInputField
              ref={nameRef}
              defaultValue={name}
              placeholder="Enter a task description"
              onBlur={updateName}
            />
          )}
        </div>
        <div
          className="nc-checklist__due-date"
          onClick={onEditMode}
        >
          <ChecklistDueDate
            date={dueDate}
            timeframe={timeframe}
            module={module}
            isEditing={isEditing}
            placeholder="Timeframe"
            pushDate={pushDueDate}
            taskId={id}
            scope={scope}
            isMyFocus={isMyFocus}
            reminders={processedReminders}
            createTaskReminder={createTaskReminder}
            updateTaskReminder={updateTaskReminder}
            deleteTaskReminder={deleteTaskReminder}
          />
        </div>
        {isEditing && (
          <div className="nc-checklist__detailed-view">
            <textarea
              className="nc-checklist__details"
              placeholder="details"
              defaultValue={details}
              onBlur={updateDetails}
            />
            <div className="nc-checklist__assignment">
              <ChecklistAssigned
                taskId={id}
                assignments={assignments}
                assignmentOptions={assignmentOpts}
              />
              {(orgId === organizationId || orgId === weddingItem?.wedding?.organizationId) && (
                <ChecklistVisibility
                  selected={visibility}
                  onChange={updateVisibility}
                />
              )}
            </div>
            <ChecklistComments
              taskId={id}
              comments={comments}
              meId={userId}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default NCTask;
